import {
  IngredientGroup,
  SupplementLearnMore,
  SupplementProduct,
} from 'content/products/supplements';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

// Kill-switch for the whole Women's nutrition (UH x Luminary) group on
// /shop. Single source of truth for launch-readiness, ANDed into
// `isLuminaryActive` in AllProducts.tsx.
//
// KEEP THIS `true` ONLY while the remaining launch items are open. Variant IDs,
// copy and images are all REAL now, so add-to-cart is LIVE and the cards show
// real product shots. The display price is now $74 (see PRICE note below) —
// the Shopify variant must also be updated to $74 so checkout matches the
// displayed price. Flip to a permanent `true` only once that's confirmed and
// legal has signed off on the copy + disclaimers.
export const LUMINARY_ENABLED = true;

// ---------------------------------------------------------------------------
// Women's nutrition — Luminary Vitamins gummy supplements (US-only, v1)
// ---------------------------------------------------------------------------
//
// Distinct, US-only supplements group surfaced on /shop, separate from the
// existing (dormant) Thorne `supplementProducts`. Reuses the shared
// `SupplementProduct` shape so `ProductCardSupplements` +
// `SupplementLearnMoreModal` render it with zero component changes.
//
// 5 women's-health SKUs: preconception → pregnancy → postpartum → PCOS →
// wellbeing. Product names use Luminary's stage brand prefixes
// (Create / Form / Nurture / Balance / Thrive).
//
// Luminary also lists two men's SKUs on the same Shopify store — Men's
// Wellbeing (variant 42989751042144, SKU F7-P6IH-VTUO) and Sperm Support
// (variant 42989751271520, SKU T6-R05X-QRRS), both $89. Both are DEFERRED to a
// later phase: they post-date the feature spec, so they have no approved copy,
// and the Shopify text for Men's Wellbeing carries a "clinically validated"
// claim the spec bans outright. Do not add them back without approved copy.
//
// Sourced from the `stelcore-x-ultrahuman` Shopify store (= ShopifyStore.US in
// production, per shopifyHelper). Vendor "Luminary Vitamins", tagged
// "Shopify Collective" — i.e. Luminary fulfils, Ultrahuman lists.
//
// TITLES use the approved product/marketing names from the content brief
// (Create/Form/Nurture/Balance/Thrive). These INTENTIONALLY diverge from the
// raw Shopify product titles, so the card label differs from the cart line item
// (which Shopify supplies by variant id). Known, accepted cosmetic mismatch;
// do not "fix" it here — reconcile in Shopify if it ever matters.
//
// PRICE — the agreed price is now $74 (per the Luminary team / product-listing
// doc), down from the earlier $89. CAVEAT: the displayed price must match the
// live Shopify variant charged at checkout — if the Shopify variant is not also
// updated to $74, customers will see $74 on the card but be charged the old
// $89 at checkout. Confirm the Shopify variant price is $74 before/alongside
// shipping this change.
//
// IMAGE PATHS — real product shots live on our CDN under
// web_v2/shop/womens_nutrition/ (uploaded to s3://public-web-assets.uh-static.com/…).
// Stored as RELATIVE paths only: CustomImage prefixes SPEEDSIZE_CDN + client +
// UH_STATIC_URL (= public-web-assets.uh-static.com) to any src without a leading
// slash, so an absolute https:// URL would double-prefix and 404 — never store a
// full URL here.
//
// RESOLVED (was placeholder):
//   • US Shopify variant IDs — real, per the `data` block on each SKU.
//   • title / description / learnMore.about / ingredients / benefits /
//     dailyServing / whenToTake — from the approved content brief +
//     product-listing doc.
//   • imgSrc — real product shots under web_v2/shop/womens_nutrition/.
//   • formSize — "28 servings" for all 5 SKUs (per the 5 Aug shop-edits doc,
//     which relabels the card prefix from "28 gummies" to "28 servings"), shown
//     as the card description prefix. Card-only; the modal does not render it.
//     (Daily serving differs per SKU and lives in the modal's Daily serving
//     field — e.g. Create/Form are 2 gummies/day — not on the card.)
//
// RESOLVED — "28 servings" is correct for all 5 SKUs. Luminary's product-listing
// doc states "Servings Per Container: 28" on every SKU, and a Create/Form
// serving is 1 packet (2 gummies), so those two packs hold 56 gummies in 28
// packets. The old "28 gummies" label was the wrong number, not the new one.
//
// COMPLIANCE — copy is descriptive only. "supports egg quality" is the ceiling.
// Disclaimers are the single, common LUMINARY_DISCLAIMER below, applied to all
// 5 SKUs via `luminaryLearnMore` (see the DISCLAIMER note below).
//
// ⚠️ Flagged for legal sign-off (sits near the spec's hard bans). Updated
// after the 17 Sep USF physician review, which WIDENED several of these rather
// than narrowing them:
//   • the section subtitle "…for conception…" (near the banned
//     conception-outcome line);
//   • claims about the baby rather than the woman, now broader than before —
//     Form's benefit is "Choline supports healthy placental and fetal
//     development, particularly fetal brain development" (was fetal brain only),
//     Form's about "…support both mother and baby…", Create's benefits carry
//     BOTH "Folic acid supports healthy fetal development when taken before
//     pregnancy" and NEW "Choline supports healthy fetal and placental
//     development" — two fetal-development claims on a pre-pregnancy product —
//     and NEW: Nurture's benefits now make two claims about the baby
//     ("…supports the baby's brain development and neurocognition",
//     "Inositol supports the baby's brain and cognitive development");
//   • "egg health" is now "egg quality" throughout, per the physician review.
//     Note this is the MORE specific claim, not the safer one;
//   • Balance's about names PCOS, and the LP's Cycle paragraph now opens on
//     "Menstrual irregularity is usually a hallmark of PMOS" — note the two
//     surfaces use DIFFERENT terms (PCOS here, PMOS on the LP per the source
//     doc). See the disease-claim note in
//     components/luminary/NutrientsSection.tsx.
//
// The earlier flag on the Pregnancy subtitle ("…in one gummy instead of a
// prenatal pill") is resolved: that line is gone, both because it implied
// substitution and because Form is 2 gummies/day, not one.
//
// INGREDIENTS — now the FULL Supplement Facts panels from Luminary's
// brand-guidelines + product-listing doc, not the earlier key-ingredients
// subset. See the ingredients block below for the source link and the
// essentials-then-base ordering rule. Thrive's lycopene (10 mg) is back, since
// the full panel carries it; its "Lycopene for antioxidant support" benefit is
// still absent, because the 5 Aug shop-edits benefit list does not include it.
//
// NOTE: Form's iron is 27 mg, not the 9 mg previously listed, so it is a
// standard-strength prenatal on iron. Do not write copy positioning it as
// gentler or lower-iron than a conventional prenatal.
// ---------------------------------------------------------------------------

// DISCLAIMER — approved common wording, now applied to all 5 SKUs. Every
// product's `learnMore.disclaimer` is set to this single string, so the modal
// renders it as the Disclaimer section on every card (SupplementLearnMoreModal
// only shows that section when a product has disclaimer copy).
// Shortened to the FDA structure/function statement + physician-formulated
// line (4 Aug). The earlier long-form paragraph (hypersensitivity, sweetener
// note, above-standard-intake folate/iron, consult-your-practitioner,
// not-a-fertility-treatment, metrics-don't-measure, reseller/attribution) is
// gone with it — if legal asks for any of those clauses back, restore them
// here from git history.
const LUMINARY_DISCLAIMER =
  'These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease. Formulated by physicians for nutritional support.';

// SHIPPING — Luminary fulfils via Shopify Collective, on a different timeline
// than the (dormant) Thorne supplements, so this overrides the modal's default
// shipping line via `SupplementProduct.shippingNote` on all 5 SKUs below.
const LUMINARY_SHIPPING =
  'Gummies ship within 5-10 business days of purchase. If ordered with other items, they may ship separately.';

// Card quantity prefix ("28 servings | …" on the card description). Card-only —
// the modal does not render formSize. Relabelled from "28 gummies" to
// "28 servings" per the 5 Aug shop-edits doc, and confirmed by Luminary's
// product-listing doc ("Servings Per Container: 28" on all 5). (Daily serving
// differs by SKU — Create/Form are 1 packet of 2 gummies, the rest 1 gummy —
// and lives in the modal's "Daily serving" field, not here.)
const LUMINARY_FORM_SIZE = '28 servings';

// ---------------------------------------------------------------------------
// Ingredients — full Supplement Facts panels
// ---------------------------------------------------------------------------
// Source: the Luminary brand-guidelines + product-listing Google Doc
// (docs.google.com/document/d/1xiPGMGCbhsc5g36Bwsbl_X3AXfoeoaZDuYpGrLTQ4R0),
// "Product Listing" section. This SUPERSEDES the earlier key-ingredients-only
// lists, which carried 4-5 headline actives per SKU. The panels are now
// complete, so the "Ingredients are partial" caveat no longer applies.
//
// ORDER — segmented into "Essential" and "Other", mirroring the source doc's
// split (requested 6 Aug 2026; this REVERSES the earlier flat panel-order rule,
// which existed only because the modal had no group headings — it renders
// titled groups now). "Essential" carries the SKU's headline actives,
// "Other" the shared micronutrient base; within each group the order follows
// the source doc. The split lives in `ingredientGroups`; the flat
// `ingredients` array is still populated (groups flattened in order) by the
// factory below for anything that reads it.
//
// BALANCE is the exception: the doc does not split its 2-ingredient panel, so
// it keeps a flat, ungrouped `ingredients` list — no headings in the modal.
//
// The shared "Other" base is still written out in full per SKU rather than
// spread in from a shared constant. Accepted — this is data, and the doses
// diverge between SKUs anyway (Thrive's B6/B12/iron differ from the rest).
//
// SPELLING — the doc misspells three standard chemical names ("Uniquinone",
// "Choline L-Bitrartrate", "Nicontinamide"). Corrected here to ubiquinone,
// choline L-bitartrate and nicotinamide.
//
// FOLATE is stated as mcg DFE with the folic acid amount in parentheses, which
// is how the FDA requires it on a Supplement Facts panel. Do not simplify to a
// bare folic acid figure — the two numbers are not interchangeable. Prose copy
// (card descriptions, about, benefits, the LP nutrients paragraphs) says "folic
// acid" per Shravani's 31 Aug call; the three panel lines keep "Folate (as
// folic acid …)" because the panel format is regulatory, not editorial.

const luminaryLearnMore = ({
  about,
  ingredients,
  ingredientGroups,
  benefits,
  dailyServing,
  whenToTake,
}: {
  about: string;
  // Pass exactly one: `ingredientGroups` (Essential/Other split) for the
  // segmented SKUs, or `ingredients` (flat panel) for Balance. The flat list
  // is derived from the groups so it always carries the full panel.
  ingredients?: string[];
  ingredientGroups?: IngredientGroup[];
  benefits: string[];
  dailyServing: string;
  whenToTake: string;
}): SupplementLearnMore => ({
  about,
  ingredients:
    ingredients ?? (ingredientGroups ?? []).flatMap((group) => group.items),
  ...(ingredientGroups ? { ingredientGroups } : {}),
  benefits,
  dailyServing,
  whenToTake,
  disclaimer: LUMINARY_DISCLAIMER,
});

// All SKUs display at $74 USD — the agreed price per the Luminary team /
// listing doc. See the PRICE note in the header: the live Shopify variant
// must also be $74, or customers see $74 but are charged the old price at
// checkout.
const LUMINARY_US_PRICE = 74;

// ---------------------------------------------------------------------------
// Product copy
// ---------------------------------------------------------------------------
// `*_SUBTITLE` → the card `description` (short marketing line from the content
// brief). `*_ABOUT` → the modal `learnMore.about` (the fuller description from
// the product-listing doc). The two are intentionally decoupled: concise on the
// card, fuller in the PDP modal.
// ---------------------------------------------------------------------------

const PRECONCEPTION_SUBTITLE =
  'Doctor-formulated with CoQ10, folic acid, choline, and other essential nutrients to support egg quality and preconception nutrition.';
const PREGNANCY_SUBTITLE =
  'Doctor-formulated with folic acid, choline, iron, vitamin D, and other essential nutrients to support nutritional needs throughout pregnancy.';
const POSTPARTUM_SUBTITLE =
  'Choline, folic acid, iron, B vitamins, and inositol to support postpartum nutrition, energy, and overall wellbeing.';
const HORMONAL_HEALTH_SUBTITLE =
  'A clinically studied 40:1 ratio of myo-inositol to D-chiro-inositol to support cycle regularity, hormonal health, and ovarian function.';
const WELLBEING_SUBTITLE =
  'A comprehensive women’s multivitamin with 15+ essential nutrients to support energy, metabolism, immune health, and everyday wellbeing.';

const PRECONCEPTION_ABOUT =
  'Doctor-formulated, pill-free nutrient gummies featuring clinically studied ingredients selected to support egg quality, cellular energy, and nutritional needs before pregnancy. Vegan, no added sugar, and third-party verified.';
// The earlier partner-supplied Form description is gone as of the 5 Aug
// shop-edits doc, and with it the three problems it carried: "clinically
// validated" (hard-ban list), "a smarter, more effective alternative to
// one-size-fits-all prenatals" (comparative efficacy claim against the whole
// prenatal category), and the baby brain / bone / neural development claim.
// ⚠️ What remains to clear with legal: "to support both mother and baby
// throughout pregnancy" is still a claim about the baby, not the woman.
const PREGNANCY_ABOUT =
  'Form nutrient gummies provide essential pregnancy support in a doctor-formulated, pill-free format. Featuring folic acid, choline, iron, vitamin D, and other important nutrients to support both mother and baby throughout pregnancy. Vegan, no added sugar, and third-party verified.';
const POSTPARTUM_ABOUT =
  'Created specifically for the postpartum stage, Nurture provides targeted nutritional support for the changing needs of new mothers. These doctor-formulated, pill-free nutrient gummies feature iron, choline, folic acid, B vitamins, and inositol to support energy, metabolism, and overall postpartum wellbeing. Vegan, no added sugar, and third-party verified.';
// ⚠️ This is the first Luminary string to name PCOS on the site. The card title
// deliberately drops "& PCOS" to keep the disease term off the card (see the
// title note on SKU 104), so naming it in the modal needs legal's sign-off. It
// is framed as "nutritional support", not treatment, which is the ceiling.
const HORMONAL_HEALTH_ABOUT =
  'Balance nutrient gummies feature a clinically studied 40:1 ratio of myo-inositol to D-chiro-inositol. Doctor-formulated to support cycle regularity, hormonal health, ovarian function, and glucose metabolism, particularly for women seeking targeted PCOS nutritional support. Vegan, no added sugar, and third-party verified.';
const WELLBEING_ABOUT =
  'Thrive is a comprehensive women’s multivitamin reimagined as a doctor-formulated, pill-free nutrient gummy. Featuring essential vitamins, minerals, CoQ10, L-carnitine, and inositol to support energy, metabolism, immune health, and overall wellbeing. Vegan, no added sugar, and third-party verified.';

const usData = (shopifyVariantId: string) => ({
  [ShopifyStore.US]: {
    shopifyVariantId: `gid://shopify/ProductVariant/${shopifyVariantId}`,
    price: LUMINARY_US_PRICE,
  },
});

export const luminaryProducts: SupplementProduct[] = [
  {
    id: 101,
    sku: 'F4-V44I-TBIU',
    title: 'Create: Preconception Gummies',
    description: PRECONCEPTION_SUBTITLE,
    formSize: LUMINARY_FORM_SIZE,
    price: LUMINARY_US_PRICE,
    imgSrc: 'web_v2/shop/womens_nutrition/create.png',
    tag: 'new',
    shippingNote: LUMINARY_SHIPPING,
    learnMore: luminaryLearnMore({
      about: PRECONCEPTION_ABOUT,
      // 18 nutrients: 5 essential + 13 base, doc order.
      ingredientGroups: [
        {
          title: 'Essential',
          items: [
            'CoQ10 (as ubiquinone): 600 mg',
            'Iron (as ferrous citrate): 27 mg',
            'Folate (as folic acid 1,200 mcg): 2,000 mcg DFE',
            'Choline (as choline L-bitartrate): 100 mg',
            'Vitamin D (as cholecalciferol from algae): 50 mcg / 2,000 IU',
          ],
        },
        {
          title: 'Other',
          items: [
            'Vitamin A (as retinol acetate): 770 mcg',
            'Vitamin C (as ascorbic acid): 120 mg',
            'Vitamin E (as DL-alpha-tocopheryl acetate): 19 mg',
            'Thiamin (as thiamine hydrochloride): 1.4 mg',
            'Riboflavin: 1.4 mg',
            'Niacin (as nicotinamide): 18 mg',
            'Vitamin B6 (as pyridoxine hydrochloride): 2 mg',
            'Vitamin B12 (as cyanocobalamin): 16 mcg',
            'Biotin (as D-biotin on dicalcium phosphate): 30 mcg',
            'Vitamin K2 (as menaquinone-7): 90 mcg',
            'Iodine (as potassium iodide): 220 mcg',
            'Zinc (as zinc citrate dihydrate): 11 mg',
            'Selenium (from selenium yeast): 60 mcg',
          ],
        },
      ],
      benefits: [
        'Supports preconception nutrition.',
        'CoQ10 supports egg quality.',
        'Folic acid supports healthy fetal development when taken before pregnancy.',
        'Choline supports healthy fetal and placental development.',
      ],
      dailyServing: '2 gummies (1 packet)',
      whenToTake:
        'As soon as you begin trying to conceive, or at least 3 months prior to planned conception.',
    }),
    data: usData('42989751664736'),
  },
  {
    id: 102,
    sku: 'YR-2GW6-D6H2',
    title: 'Form: Pregnancy Gummies',
    description: PREGNANCY_SUBTITLE,
    formSize: LUMINARY_FORM_SIZE,
    price: LUMINARY_US_PRICE,
    imgSrc: 'web_v2/shop/womens_nutrition/form.png',
    tag: 'new',
    shippingNote: LUMINARY_SHIPPING,
    learnMore: luminaryLearnMore({
      about: PREGNANCY_ABOUT,
      // 17 nutrients: 4 essential + 13 base, doc order. Form carries no CoQ10 —
      // it is the only difference from Create's panel apart from choline
      // (300 mg vs 100 mg).
      ingredientGroups: [
        {
          title: 'Essential',
          items: [
            'Folate (as folic acid 1,200 mcg): 2,000 mcg DFE',
            'Choline (as choline L-bitartrate): 300 mg',
            'Iron (as ferrous citrate): 27 mg',
            'Vitamin D (as cholecalciferol from algae): 50 mcg / 2,000 IU',
          ],
        },
        {
          title: 'Other',
          items: [
            'Vitamin A (as retinol acetate): 770 mcg',
            'Vitamin C (as ascorbic acid): 120 mg',
            'Vitamin E (as DL-alpha-tocopheryl acetate): 19 mg',
            'Thiamin (as thiamine hydrochloride): 1.4 mg',
            'Riboflavin: 1.4 mg',
            'Niacin (as nicotinamide): 18 mg',
            'Vitamin B6 (as pyridoxine hydrochloride): 2 mg',
            'Vitamin B12 (as cyanocobalamin): 16 mcg',
            'Biotin (as D-biotin on dicalcium phosphate): 30 mcg',
            'Vitamin K2 (as menaquinone-7): 90 mcg',
            'Iodine (as potassium iodide): 220 mcg',
            'Zinc (as zinc citrate dihydrate): 11 mg',
            'Selenium (from selenium yeast): 60 mcg',
          ],
        },
      ],
      benefits: [
        'Folic acid supports healthy fetal development.',
        'Choline supports healthy placental and fetal development, particularly fetal brain development.',
        'Iron supports healthy red blood cell formation.',
        'Vitamin D supports immune and bone health.',
      ],
      dailyServing: '2 gummies (1 packet)',
      whenToTake:
        'As soon as you get a positive pregnancy test, through all nine months.',
    }),
    data: usData('42989751468128'),
  },
  {
    id: 103,
    sku: 'RW-FBCV-VG4J',
    title: 'Nurture: Postpartum Gummies',
    description: POSTPARTUM_SUBTITLE,
    formSize: LUMINARY_FORM_SIZE,
    price: LUMINARY_US_PRICE,
    imgSrc: 'web_v2/shop/womens_nutrition/nurture.png',
    tag: 'new',
    shippingNote: LUMINARY_SHIPPING,
    learnMore: luminaryLearnMore({
      about: POSTPARTUM_ABOUT,
      // 18 nutrients: 5 essential + 13 base, doc order. Nurture's folate is
      // the low one across the range: 667 mcg DFE, against 2,000 for Create
      // and Form.
      ingredientGroups: [
        {
          title: 'Essential',
          items: [
            'Folate (as folic acid 400 mcg): 667 mcg DFE',
            'Choline (as choline L-bitartrate): 200 mg',
            'Inositol (as myo-inositol): 300 mg',
            'Iron (as ferrous citrate): 27 mg',
            'Vitamin D (as cholecalciferol from algae): 50 mcg / 2,000 IU',
          ],
        },
        {
          title: 'Other',
          items: [
            'Vitamin A (as retinol acetate): 770 mcg',
            'Vitamin C (as ascorbic acid): 120 mg',
            'Vitamin E (as DL-alpha-tocopheryl acetate): 19 mg',
            'Thiamin (as thiamine hydrochloride): 1.4 mg',
            'Riboflavin: 1.4 mg',
            'Niacin (as nicotinamide): 18 mg',
            'Vitamin B6 (as pyridoxine hydrochloride): 2 mg',
            'Vitamin B12 (as cyanocobalamin): 16 mcg',
            'Biotin (as D-biotin on dicalcium phosphate): 30 mcg',
            'Vitamin K2 (as menaquinone-7): 90 mcg',
            'Iodine (as potassium iodide): 220 mcg',
            'Zinc (as zinc citrate dihydrate): 11 mg',
            'Selenium (from selenium yeast): 60 mcg',
          ],
        },
      ],
      benefits: [
        'Iron supports healthy red blood cell formation.',
        'Choline intake enriches breast milk, which supports the baby’s brain development and neurocognition.',
        'Inositol supports the baby’s brain and cognitive development while supporting maternal metabolism.',
        'Purposefully formulated for postpartum nutrition.',
      ],
      dailyServing: '1 gummy',
      whenToTake:
        'For at least the first year after giving birth, or as long as you’re breastfeeding.',
    }),
    data: usData('42989751894112'),
  },
  {
    id: 104,
    sku: 'MK-SXFA-4S7I',
    // Content brief names this "Balance: Hormonal Health Gummies"; the Shopify
    // title adds "& PCOS". We use the brief name (drops "& PCOS") — this also
    // keeps the disease term out of the card title.
    title: 'Balance: Hormonal Health Gummies',
    description: HORMONAL_HEALTH_SUBTITLE,
    formSize: LUMINARY_FORM_SIZE,
    price: LUMINARY_US_PRICE,
    imgSrc: 'web_v2/shop/womens_nutrition/balance.png',
    tag: 'new',
    shippingNote: LUMINARY_SHIPPING,
    learnMore: luminaryLearnMore({
      about: HORMONAL_HEALTH_ABOUT,
      // Balance is a 2-ingredient formula — the panel carries no micronutrient
      // base, so it is genuinely this short. Not a truncated list. The source
      // doc doesn't split this panel, so Balance is the one SKU that stays
      // ungrouped (flat list, no Essential/Other headings in the modal).
      ingredients: [
        'Myo-inositol: 2,000 mg',
        'D-chiro-inositol: 50 mg (40:1 ratio)',
      ],
      benefits: [
        'Supports cycle regularity.',
        'Supports overall hormonal health.',
        'Supports ovarian function.',
        'Supports glucose metabolism and metabolic health.',
        'Clinically studied 40:1 ratio of myo-inositol to D-chiro-inositol.',
      ],
      dailyServing: '1 gummy',
      whenToTake: 'Daily',
    }),
    data: usData('42989489586272'),
  },
  {
    id: 105,
    sku: 'OG-X6GR-SABJ',
    // Content brief names this "Thrive: Wellbeing Gummies" (the women's Thrive
    // formula). Revisit if the deferred "Men's Wellbeing Gummies" ever lands
    // beside it.
    title: 'Thrive: Wellbeing Gummies',
    description: WELLBEING_SUBTITLE,
    formSize: LUMINARY_FORM_SIZE,
    price: LUMINARY_US_PRICE,
    imgSrc: 'web_v2/shop/womens_nutrition/thrive.png',
    tag: 'new',
    shippingNote: LUMINARY_SHIPPING,
    learnMore: luminaryLearnMore({
      about: WELLBEING_ABOUT,
      // 19 nutrients: 5 essential + 14 base, doc order — this is what
      // substantiates the card's "15+ essential nutrients". Thrive is the
      // outlier on doses: B6 is 25 mg (2 mg elsewhere), B12 2.6 mcg (16 mcg
      // elsewhere), iron 9 mg (27 mg elsewhere, and in "Other" here per the
      // doc), and it carries no folate or choline at all. Lycopene is back —
      // the full panel carries it at 10 mg, so the earlier removal (it was
      // absent from the 29 Jul key-ingredients list) is reversed.
      ingredientGroups: [
        {
          title: 'Essential',
          items: [
            'CoQ10 (as ubiquinone): 300 mg',
            'Inositol (as myo-inositol and D-chiro-inositol): 307.5 mg',
            'Vitamin D (as cholecalciferol from algae): 50 mcg / 2,000 IU',
            'L-carnitine: 300 mg',
            'Lycopene: 10 mg',
          ],
        },
        {
          title: 'Other',
          items: [
            'Vitamin A (as retinol acetate): 770 mcg',
            'Vitamin C (as ascorbic acid): 120 mg',
            'Vitamin E (as DL-alpha-tocopheryl acetate): 19 mg',
            'Thiamin (as thiamine hydrochloride): 1.4 mg',
            'Riboflavin: 1.4 mg',
            'Niacin (as nicotinamide): 18 mg',
            'Vitamin B6 (as pyridoxine hydrochloride): 25 mg',
            'Vitamin B12 (as cyanocobalamin): 2.6 mcg',
            'Biotin (as D-biotin on dicalcium phosphate): 30 mcg',
            'Vitamin K2 (as menaquinone-7): 90 mcg',
            'Iron (as ferrous citrate): 9 mg',
            'Iodine (as potassium iodide): 220 mcg',
            'Zinc (as zinc citrate dihydrate): 11 mg',
            'Selenium (from selenium yeast): 60 mcg',
          ],
        },
      ],
      benefits: [
        'Supports everyday energy and wellbeing.',
        'CoQ10 and L-carnitine support cellular energy.',
        'Inositol supports healthy metabolism.',
        'Essential nutrients support immune, bone, and skin health.',
      ],
      dailyServing: '1 gummy',
      whenToTake: 'Daily',
    }),
    data: usData('42984301264992'),
  },
];
